import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { routeFunction } from '@/lib/functionVisibility';

const CompanyFunctionsContext = createContext(null);
const CHANGE_EVENT = 'billfac-company-functions-changed';

export const CompanyFunctionsProvider = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const identity = user ? `${user.id || user._id || user.email}:${user.role}:${user.verkoper_id || ''}` : '';
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const version = useRef(0);
  const savingRef = useRef(false);
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null);
  const data = snapshot?.identity === identity ? snapshot.data : null;
  const invalidatePendingReads = useCallback(() => { version.current++; }, []);

  const reload = useCallback(async () => {
    if (!identity || savingRef.current === identity) return;
    const request = ++version.current;
    try {
      const r = await API.get('/instellingen/functies', { noCache: true, timeout: 15000 });
      if (request !== version.current || identityRef.current !== identity) return;
      setSnapshot({ identity, data: r.data });
      setError('');
    } catch {
      if (request === version.current && identityRef.current === identity)
        setError('Functies konden niet worden geladen. Probeer opnieuw.');
    }
  }, [identity]);

  useEffect(() => {
    reload();
    const visible = () => { if (!document.hidden) reload(); };
    const changed = e => { if (e.key === CHANGE_EVENT) reload(); };
    window.addEventListener('focus', reload);
    window.addEventListener('storage', changed);
    document.addEventListener('visibilitychange', visible);
    return () => {
      invalidatePendingReads();
      window.removeEventListener('focus', reload);
      window.removeEventListener('storage', changed);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [reload, pathname, invalidatePendingReads]);

  const update = async (key, actief) => {
    if (savingRef.current || !data?.mag_wijzigen || key === 'instellingen') return;
    savingRef.current = identity;
    version.current++; // Discard reads begun before this write.
    setSaving(key);
    setError('');
    try {
      const r = await API.patch('/instellingen/functies', { functies: { [key]: actief } }, { timeout: 15000 });
      if (identityRef.current !== identity) return;
      setSnapshot({ identity, data: r.data });
      try { localStorage.setItem(CHANGE_EVENT, String(Date.now())); } catch { /* No local persistence required. */ }
    } catch {
      if (identityRef.current === identity)
        setError('Wijziging niet bevestigd. De laatst bevestigde instelling blijft zichtbaar; probeer opnieuw.');
    } finally {
      savingRef.current = false;
      setSaving(null);
    }
  };

  const isVisible = useCallback(url => {
    if (!url) return false;
    if (url === '/super-admin' || url === '/instellingen' || url.startsWith('/instellingen/')) return true;
    if (!data) return false;
    const key = routeFunction(url, data.functies);
    return !key || data.functies[key] !== false;
  }, [data]);

  return <CompanyFunctionsContext.Provider value={{ data, error, saving, update, reload, isVisible }}>
    {children}
  </CompanyFunctionsContext.Provider>;
};

export const useCompanyFunctions = () => useContext(CompanyFunctionsContext);